import { useCallback, useEffect, useRef, useState } from "react";
import { ExportDialog } from "./components/deck/ExportDialog";
import "@/App.css";
import { AnimatePresence, motion } from "framer-motion";
import { DeckProvider, SECTIONS, useDeck } from "./lib/deck-context";
import { Sidebar } from "./components/deck/Sidebar";
import { TopBar, Pager } from "./components/deck/TopBar";
import { PresentControls } from "./components/deck/PresentControls";
import { AssumptionsPanel } from "./components/deck/AssumptionsPanel";
import { SECTION_COMPONENTS } from "./components/sections";
import { LoginGate } from "./components/deck/LoginGate";
import { Toaster } from "./components/ui/sonner";

function Shell() {
  const { idx, next, prev, present, setPresent, panelOpen } = useDeck();
  const mainRef = useRef(null);
  const [exportOpen, setExportOpen] = useState(false);

  const enterPresent = useCallback(() => {
    setPresent(true);
    document.documentElement.requestFullscreen?.().catch(() => {});
  }, [setPresent]);
  const exitPresent = useCallback(() => {
    setPresent(false);
    if (document.fullscreenElement) document.exitFullscreen?.();
  }, [setPresent]);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (panelOpen || ["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight" || e.key === "PageDown") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Escape" && present) exitPresent();
    };
    const onFs = () => { if (!document.fullscreenElement && present) setPresent(false); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => { window.removeEventListener("keydown", onKey); document.removeEventListener("fullscreenchange", onFs); };
  }, [next, prev, present, panelOpen, exitPresent, setPresent]);

  const Current = SECTION_COMPONENTS[SECTIONS[idx].id];
  return (
    <div data-testid="app-shell" data-present={present} className="flex h-screen bg-paper text-char">
      {!present && <Sidebar onPresent={enterPresent} onExport={() => setExportOpen(true)} />}
      <main ref={mainRef} className="flex-1 h-screen overflow-y-auto">
        {!present && <TopBar onPresent={enterPresent} />}
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={present ? "pb-24" : ""}
          >
            <Current />
          </motion.div>
        </AnimatePresence>
        {!present && <Pager />}
      </main>
      {present && <PresentControls onExit={exitPresent} />}
      <AssumptionsPanel />
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
      <Toaster position="bottom-right" />
    </div>
  );
}

export default function App() {
  return (
    <LoginGate>
      <DeckProvider>
        <Shell />
      </DeckProvider>
    </LoginGate>
  );
}
